import memoize from 'lodash/memoize';

import type { GroupBase, MultiValue, SingleValue } from 'react-select-v5';

const isGroup = <O, G extends GroupBase<O>>(optionOrGroup: G | unknown): optionOrGroup is GroupBase<O> =>
  (optionOrGroup as GroupBase<O>).options != null;

const isMultiValue = <T>(arg: MultiValue<T> | SingleValue<T>): arg is MultiValue<T> => Array.isArray(arg);

const findOption =
  <O, G extends GroupBase<O>>(valueKey: string, optionOrGroups: readonly (O | G)[] = []) =>
  (value: O): O => {
    for (const optionOrGroup of optionOrGroups) {
      if (isGroup(optionOrGroup)) {
        const option = findOption(valueKey, optionOrGroup.options)(value);
        if (typeof option === 'object') {
          return option;
        }
      } else if (value && typeof value === 'object') {
        if (value[valueKey] === optionOrGroup[valueKey]) {
          return optionOrGroup;
        }
      } else if (value === optionOrGroup[valueKey]) {
        return optionOrGroup;
      }
    }

    return value;
  };

const getReactSelectStyles = memoize((multi, isFixedOption, styles = {}) => {
  const multiStyles = multi
    ? {
        multiValue: (base, state) => {
          const isFixed = typeof isFixedOption === 'function' && isFixedOption(state.data);
          return isFixed ? { ...base, backgroundColor: 'gray' } : base;
        },
        multiValueLabel: (base, state) => {
          const isFixed = typeof isFixedOption === 'function' && isFixedOption(state.data);
          return isFixed ? { ...base, fontWeight: 'bold', color: 'gray' } : base;
        },
        multiValueRemove: (base, state) => {
          const isFixed = typeof isFixedOption === 'function' && isFixedOption(state.data);
          return isFixed ? { ...base, display: 'none' } : base;
        },
      }
    : {};

  return {
    ...styles,
    ...multiStyles,
  };
});

const tryToStringify = (value) => {
  try {
    return JSON.stringify(value);
  } catch (err) {
    return value;
  }
};

const valueToOption = (value, options, valueKey = 'id') => {
  if (!options) {
    return value;
  } else if (value && typeof value === 'object' && Object.keys(value).length > 1) {
    return value;
  }
  const optionFromValue = options.find((option) => [option, option[valueKey]].includes(value[valueKey]));
  return optionFromValue || value;
};

export { isGroup, isMultiValue, findOption, getReactSelectStyles, tryToStringify, valueToOption };
